import React from 'react';
import PropTypes from 'prop-types';
import { action, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter, routerShape } from 'react-router';
import * as Sentry from '@sentry/react';

import {withI18n} from 'spa/Components/Context';
import { BOOKING_BASE_URL } from 'utils/constants';
import autobind from 'autobind-decorator';
import isValidException from 'utils/isValidException';

import Button, {
  ButtonSizeTypes,
  ButtonType,
} from '@urbanmassage/component-library/lib/Button/Button';
import Link from '@urbanmassage/component-library/lib/Link/Link';
import Typography, {TypeVariants} from '@urbanmassage/component-library/lib/Typography/Typography';

import AddressPickerSimpleRow from 'spa/Components/AddressPicker/AddressPickerSimpleRow';
import LoadingIndicator from 'spa/Components/LoadingIndicator';

import './availability-book-status-info.less';

@withRouter
@inject(({
  store: {
    basketNew,
    notifications,
    tracker,
  },
}) => (
  {
    basketNew,
    notifications,
    tracker,
  }
))
@observer
@withI18n
class AvailabilityBookStatusInfo extends React.Component {
  static propTypes = {
    alignLeft: PropTypes.bool,
    basketNew: PropTypes.shape({
      basketHasAddress: PropTypes.bool,
      basketFormattedAddress: PropTypes.string,
      basketsAppointmentsAddress: PropTypes.shape({
        put: PropTypes.func.isRequired,
      }).isRequired,
      getBasketSummary: PropTypes.func.isRequired,
    }).isRequired,
    isLoadingStatus: PropTypes.bool,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    onClickBookButton: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
      trackCriteoEvent: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    trackingLocation: PropTypes.string,
    unavailableReason: PropTypes.string,
  }

  componentDidMount() {
    const {
      basketNew,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    tracker.track('Shown Availability Book Status Info', {
      category: trackingCategory,
      location: trackingLocation,
    });

    basketNew.getBasketSummary();
  }

  @observable basketHasAddress = false;
  @observable isLoading = false;

  @autobind
  @action('onClickBook - AvailabilityBookStatusInfo')
  onClickBook() {
    const {
      onClickBookButton,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    tracker.track(`Clicked book button in the Availability Book Status Info in ${trackingCategory}`, {
      category: trackingCategory,
      location: trackingLocation,
    });

    onClickBookButton();
  }

  @autobind
  @action('onClickUseAddress - AvailabilityBookStatusInfo')
  onClickUseAddress(selectedAddress) {
    const {i18n} = this.context;
    const {
      basketNew,
      basketNew: {
        basketsAppointmentsAddress,
      },
      notifications,
      router,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    const trackingData = {
      category: trackingCategory,
      label: selectedAddress,
    };

    if (trackingLocation) {
      trackingData.data = {
        page: trackingLocation,
      };
    }

    tracker.track(`Clicked add address to basket in ${trackingCategory}`, trackingData);

    this.isLoading = true;

    basketsAppointmentsAddress.put('', selectedAddress)
      .then((response) => {
        this.isLoading = false;

        basketNew.getBasketSummary()
          .catch((err) => {
            if (__DEV__) {
              console.error(err);
            }
          });
      }, (error) => {
        runInAction(() => {
          if (error.json && error.json.code === 'ADDRESS_NOT_SERVICED') {
            router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/address?notServiced=true`);
          } else {
            if (__DEV__) {
              console.error(error);
            }
            notifications.add({
              id: 'addAddressBasketError',
              type: 'error',
              title: i18n.__error(error),
            });

            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Unhandled error adding address to basket appointment');
            }
          }

          this.isLoading = false;
        });
      }).catch((err) => {
        if (__DEV__) {
          console.error(err);
        }

        this.isLoading = false;

        // This is unexpected so record it in Sentry
        if (isValidException(err)) {
          Sentry.captureException(err);
        } else {
          Sentry.captureMessage('Unhandled error adding address to basket appointment');
        }
      });
  }

  render() {
    const { i18n } = this.context;
    const {
      alignLeft,
      basketNew,
      isLoadingStatus,
      unavailableReason,
    } = this.props;
    const {
      isLoading,
      onClickBook,
      onClickUseAddress,
    } = this;

    if (isLoadingStatus) {
      return (
        <div
          className="availability-book-status-info__loading-container"
          data-testid="availability-book-status-info-loading-container"
        >
          <LoadingIndicator style={{color: '#333'}} />
        </div>
      );
    }

    return (
      <div className="availability-book-status-info" data-testid="availability-book-status-info">
        {basketNew.basketHasAddress ? (
          <React.Fragment>
            <Typography
              align={alignLeft ? 'left' : 'center'}
              component={TypeVariants.body}
              data-testid="availability-book-status-info-address-info"
              margin="1.5rem 0"
              variant={TypeVariants.titleXS}
            >
              {i18n._x('Booking at %(formattedAddress)s', 'Availability to Book Status Info component book section', {
                formattedAddress: basketNew.basketFormattedAddress,
              })}
            </Typography>
            {!unavailableReason ? (
              <React.Fragment>
                <Button
                  block
                  data-testid="availability-book-status-info-book-button"
                  data-qa="availability-book-status-info-book-button"
                  onClick={onClickBook}
                  size={ButtonSizeTypes.default}
                  type={ButtonType.button}
                >
                  {i18n._x('Book now', 'treatment book now button')}
                </Button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Typography
                  align={alignLeft ? 'left' : 'center'}
                  component={TypeVariants.body}
                  data-testid="availability-book-status-info-block-reason"
                  margin="1.5rem 0 0"
                  theme="standout"
                  variant={TypeVariants.titleXS}
                >
                  {unavailableReason}
                </Typography>
                <Typography
                  align={alignLeft ? 'left' : 'center'}
                  component={TypeVariants.body}
                  data-testid="availability-book-status-info-block-check-basket"
                  margin="0 0 1rem"
                  variant={TypeVariants.body}
                >
                  ({i18n.__react('Check the contents of %(basketLink)s', {
                    basketLink: <Link to={`/${i18n.lc}/${BOOKING_BASE_URL}/basket`} text={i18n._x('your basket', 'Availability to Book Status Info component book section')} />,
                  }, 'Availability to Book Status Info component book section')})
                </Typography>
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <AddressPickerSimpleRow
            customButtonTextDesktop={i18n._x('Select', 'Availability to Book Status Info component address picker button')}
            customButtonTextMobile={i18n._x('Select', 'Availability to Book Status Info component address picker button')}
            isLoading={isLoading}
            onClickSelectAddress={onClickUseAddress}
            stackButton
          />
        )}
      </div>
    );
  }
}

export default AvailabilityBookStatusInfo;



// WEBPACK FOOTER //
// ./spa/Components/AvailabilityBookStatusInfo/AvailabilityBookStatusInfo.jsx